import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useMutation } from "@tanstack/react-query";
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

import config from "../config/config";
import * as QUERIES from "../queries/query-functions";

import { NotificationContext, handleError,} from "../context/NotificationContext";
import { getBasketData, refetchBasketData } from '../queries/global';

import DonationSlider from './donation-slider';
import celebrationAnimation from '../../lottie/celebration.lottie';
import useTimedMessage from '../hook/useTimedMessage';


export default function DonationBlock({donationPrecentage, fundId, donationId, rangeMax }) {
    const STEP = 0.5;
    const { data: basketData, isPending: basketDataPending, isFetching: basketDataFetching } = getBasketData();
    const { message: successMessage, setMessage: setSuccessMessage, fadeOut } = useTimedMessage(3000);

    const { notification, setNotification } = useContext(NotificationContext); 

    // const [rangeValue, setRangeValue] = useState(0);

    const [donationAmount, setDonationAmount] = useState(0);
    // const [donationId, setDonationId] = useState(null);
    const [pending, setPending] = useState(false);
    const [donationChanged, setDonationChanged] = useState(false);
    const [animationKey, setAnimationKey] = useState(0);

    const addDonation = useMutation({
        mutationFn: () => QUERIES.addDonation([{Amount: donationAmount, FundId: fundId}]),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
        onSuccess: (data) => {
            setSuccessMessage("Donations are added to basket.");
            refetchBasketData();
        },
    });
    
    const removeDonation = useMutation({
        mutationFn: () => QUERIES.removeDonation([donationId]),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
        onSuccess: (data) => {
            refetchBasketData();
        },
    });

    const patchDonation = useMutation({
        mutationFn: (donationId) => QUERIES.patchDonation(donationId, [{ "Amount": donationAmount }]),
        onError: (error) => {
            handleError(error.message, setNotification);
        },
        onSuccess: () => {
            setSuccessMessage("Donations are added to basket.");
            refetchBasketData();
        },
    });

    const handleApplyDonation = async (e) => {
        if (!e) return;

        // If donation already exists either patch or remove it
        if (basketData?.donations.length) {
            if (donationAmount == 0) {
                removeDonation.mutateAsync(donationId);
            } else {
                patchDonation.mutateAsync(donationId);
            }
        } else {
            addDonation.mutateAsync();
        }
        setAnimationKey(prev => prev + 1);
    };

    useEffect(() => {
        const max = (donationPrecentage * rangeMax) /100;
        const donation_in_basket = basketData && basketData.donations?.find((donation) => donation.fund?.id == fundId);

        if(donation_in_basket) {
            setDonationAmount(donation_in_basket.amount);
        } else {
            setDonationAmount(Math.ceil(max));
        }
    }, [rangeMax])

    useEffect(() => {
        if (!basketData) return;

        const donation_in_basket = basketData && basketData.donations?.find((donation) => donation.fund?.id == fundId);

        if(donation_in_basket){
            setSuccessMessage("added")
        }

        setDonationChanged(!donation_in_basket);

    }, [basketData])


    // useEffect(() => {
    //     if (!basketData) return;
    //     setPending(true);

        // Find out if donation is already in the basket
        // If it is assign the basket id so it can be removed by the user
        // const donation_in_basket = basketData.donations?.find((donation) => donation.fund?.id == fundId);
        // console.log("donation_in_basket", donation_in_basket)
        // if (donation_in_basket) {
        //     setDonationId(donation_in_basket.id);
        //     // setDonationAmount(donation_in_basket.amount);

        //     // If a donation is already in the basket exit before checking donation rules
        //     setPending(false);
        //     return;
        // }
        
        // // If there is no donation in basket continue to work out donation amount
        // setDonationId(null);

        // // Check if basket total is within bounds to display prompt
        // const basket_total = basketData.total;
        // let donationAmount = null;

        // for (const rule of donationRules) {
        //     const from = parseFloat(rule.from);
        //     const to = parseFloat(rule.to);

        //     if (basket_total >= from && basket_total <= to) {
        //         donationAmount = parseFloat(rule.donation);
        //         break;
        //     }
        // }

        // // Now decide to show the prompt
        // if (donationAmount !== null) {
        //     setDonationAmount(donationAmount);
        // }

        // setPending(false);
    // }, [basketData])    



    // if (!percentage || !donationAmount || !fundId || !donationRules || !basketData || pending) return;
    
    // if (addDonation.isPending || removeDonation.isPending || basketDataFetching) return <LoadingIndicator />;

    return (
        <div className="donation-slider-block">
            <h3 className='donation-slider-block__title'>Move the slider or type an amount to choose your donation</h3>
                <div className='donation-slider__result'>
                    <div className='donation-slider__result-row'>
                        <div className='donation-slider__result-value'>
                            <span class="sr-text">Total amount </span>
                            <span className='donation-slider__result-currency'>£</span>
                            <input 
                                type='number' 
                                step={STEP} 
                                min='0'
                                className={donationChanged ? 'active': ''}
                                value={donationAmount.toFixed(2)}
                                onChange={(e) => { if(e) {
                                    setDonationAmount(Number(e.target.value));
                                    setDonationChanged(true);
                                }}} />
                        </div>
                        <div className='donation-slider__result-buttons'>
                            <button 
                                className = {`btn btn--primary ${!donationChanged ? 'fade-out' : 'fade-in'}`}
                                type = "button" 
                                disabled = { donationAmount == 0 || addDonation.isPending || removeDonation.isPending || basketDataFetching}
                                onClick={handleApplyDonation}
                                >Add donation to basket</button>
                            {successMessage && (!addDonation.isPending || !removeDonation.isPending || !basketDataFetching) && <p className={`btn btn--message ${fadeOut ? 'fade-out': ''}`}>Added to basket</p>}
                        </div>
                    </div>

                    <DonationSlider setDonationChanged={setDonationChanged} donationPrecentage={donationPrecentage} rangeValue={donationAmount} setRangeValue={setDonationAmount} STEP={STEP} rangeMax = {rangeMax} >
                        { animationKey > 0 && 
                            <div className='donation-slider__animation'>
                                <DotLottieReact
                                    src={celebrationAnimation}
                                    loop={false}
                                    autoplay
                                    key={animationKey}
                                    // onComplete={() => setShowAnimation(false)} 
                                    />
                            </div>
                        }
                    </DonationSlider>
                </div>
        </div>
    )
}